import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const hasPassword = true; // we can't know client-side; if endpoint says "current required" it will error
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => setForm({ current_password: "", new_password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password || null,
        new_password: form.new_password,
      });
      if (data.access_token) localStorage.setItem("bc_token", data.access_token);
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : (reset(), onOpenChange(false)))}>
      <DialogContent className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#1A1714] flex items-center gap-2">
            <KeyRound size={22} className="text-[#7A2021]" /> Change password
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Signed in as {user.email}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="change-password-form">
          {hasPassword && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Current password</label>
              <Input
                data-testid="cp-current"
                type="password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="mt-1 rounded-none border-[#E5E1D8] h-11"
                placeholder="Leave blank if signed up via Google"
              />
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">New password</label>
            <Input
              data-testid="cp-new"
              type="password"
              required
              minLength={6}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-1 rounded-none border-[#E5E1D8] h-11"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Confirm new password</label>
            <Input
              data-testid="cp-confirm"
              type="password"
              required
              minLength={6}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="mt-1 rounded-none border-[#E5E1D8] h-11"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="cp-submit"
            className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-11"
          >
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
